/**
 * F42 (Wave 2 — B2) — Admin venue MERGE wizard.
 *
 * Registered at `/venues/merge` + `/venues/merge/:canonicalId/:sourceId` (both
 * gated `venues:merge`). A four-step Stepper:
 *   1. Select  — search + pick the source (merged away) + canonical (kept) venue.
 *   2. Preview — per-table re-parent counts + BILLING_CONFLICT warnings, which
 *                must be acknowledged before advancing.
 *   3. Canonical — confirm/swap the canonical + a mandatory ≥10-char reason.
 *   4. Confirm — destructive dialog → POST /admin/venues/merge → result Toast.
 *
 * Venue search reads B1's `adminVenuesApi.list`; preview/merge use the B2
 * `adminVenuesMergeApi`. The merge impact-preview component is design-flagged
 * for a later polish pass (engineering ships the token-composed version here).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  Dialog,
  FormField,
  SearchInput,
  Spinner,
  Stepper,
  Table,
  type TableColumn,
  Textarea,
  useToast,
  useDebouncedValue,
} from '@bravemove/ui-web';
import {
  MERGE_REASON_MIN,
  type AdminVenueListRow,
  type MergeConflict,
  type MergeImpactPreview,
} from '@bravemove/types';
import { adminVenuesApi } from '../../api/adminVenues.js';
import { adminVenuesMergeApi } from '../../api/adminVenuesMerge.js';

type WizardStep = 'select' | 'preview' | 'canonical' | 'done';

interface VenuePick {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
}

function extractCode(err: unknown): string | null {
  if (err && typeof err === 'object') {
    const e = err as { code?: unknown; body?: { error?: { code?: unknown } } };
    if (typeof e.code === 'string') return e.code;
    if (typeof e.body?.error?.code === 'string') return e.body.error.code;
  }
  return null;
}

function rowToPick(row: AdminVenueListRow): VenuePick {
  return { id: row.id, name: row.name, slug: row.slug };
}

// ─── Venue search field ──────────────────────────────────────────────────────

function VenueSearchField({
  label,
  placeholder,
  testId,
  excludeId,
  selected,
  onSelect,
  onClear,
}: {
  label: string;
  placeholder: string;
  testId: string;
  excludeId: string | null;
  selected: VenuePick | null;
  onSelect: (pick: VenuePick) => void;
  onClear: () => void;
}) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  // F61 (A4) — the (audited) venue list is searched once typing pauses.
  const debouncedQuery = useDebouncedValue(query, 300);
  const [results, setResults] = useState<readonly VenuePick[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (selected || debouncedQuery.trim().length < 2) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    void adminVenuesApi
      .list({ q: debouncedQuery.trim(), limit: 8 })
      .then((res) => {
        if (cancelled) return;
        setResults(res.data.filter((r) => r.id !== excludeId).map(rowToPick));
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, selected, excludeId]);

  if (selected) {
    return (
      <FormField label={label}>
        <div className="flex items-center justify-between gap-3 rounded-md border border-hairline-2 bg-surface-2 px-3 py-2">
          <span className="min-w-0">
            <span className="block text-[13px] font-medium text-ink truncate">{selected.name}</span>
            <span className="block font-mono text-[11px] text-ink-3 truncate">{selected.slug}</span>
          </span>
          <Button variant="ghost" size="sm" onClick={onClear}>
            {t('admin:venueMerge.merge.canonical.swap', { defaultValue: 'Change' })}
          </Button>
        </div>
      </FormField>
    );
  }

  return (
    <FormField label={label}>
      <SearchInput
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
      />
      {searching ? (
        <div className="py-2">
          <Spinner size={18} />
        </div>
      ) : null}
      {!searching && query.trim().length >= 2 && results.length === 0 ? (
        <p className="text-[12px] text-ink-3 py-2">
          {t('admin:venueMerge.merge.select.noResults', { defaultValue: 'No matching venues' })}
        </p>
      ) : null}
      {results.length > 0 ? (
        <ul className="mt-1 flex flex-col gap-1">
          {results.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onSelect(r)}
                className="flex w-full flex-col items-start rounded-md border border-hairline-2 px-3 py-2 text-start hover:bg-surface-2"
              >
                <span className="text-[13px] font-medium text-ink">{r.name}</span>
                <span className="font-mono text-[11px] text-ink-3">{r.slug}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </FormField>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function MergeWizardPage() {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const params = useParams<{ canonicalId?: string; sourceId?: string }>();
  const [searchParams] = useSearchParams();

  const [source, setSource] = useState<VenuePick | null>(null);
  const [canonical, setCanonical] = useState<VenuePick | null>(null);
  const [step, setStep] = useState<WizardStep>('select');
  const [preview, setPreview] = useState<MergeImpactPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [prefillLoaded, setPrefillLoaded] = useState(false);

  const stepIndex = useMemo<Record<WizardStep, number>>(
    () => ({ select: 0, preview: 1, canonical: 2, done: 3 }),
    [],
  );

  const loadPreview = useCallback(
    async (src: VenuePick, canon: VenuePick) => {
      setPreviewLoading(true);
      setPreviewError(null);
      setAcknowledged(false);
      try {
        const result = await adminVenuesMergeApi.previewMerge(src.id, canon.id);
        setPreview(result);
        setStep('preview');
      } catch {
        setPreviewError(
          t('admin:venueMerge.merge.errors.generic', {
            defaultValue: 'The merge could not be completed. Please try again.',
          }),
        );
      } finally {
        setPreviewLoading(false);
      }
    },
    [t],
  );

  // Pre-fill from the URL params (the deep-linked "merge these two" entry) OR
  // from the `?source=` query param (the venue-detail "Merge…" CTA — V-10 fix:
  // that link form used to be silently ignored, opening the wizard blank).
  useEffect(() => {
    if (prefillLoaded) return;
    const { canonicalId, sourceId } = params;
    const querySourceId = searchParams.get('source');

    if (canonicalId && sourceId) {
      let cancelled = false;
      void Promise.all([adminVenuesApi.getDetail(sourceId), adminVenuesApi.getDetail(canonicalId)])
        .then(([src, canon]) => {
          if (cancelled) return;
          const srcPick = { id: src.id, name: src.name, slug: src.slug };
          const canonPick = { id: canon.id, name: canon.name, slug: canon.slug };
          setSource(srcPick);
          setCanonical(canonPick);
          void loadPreview(srcPick, canonPick);
        })
        .finally(() => {
          if (!cancelled) setPrefillLoaded(true);
        });
      return () => {
        cancelled = true;
      };
    }

    if (querySourceId) {
      let cancelled = false;
      void adminVenuesApi
        .getDetail(querySourceId)
        .then((src) => {
          if (cancelled) return;
          setSource({ id: src.id, name: src.name, slug: src.slug });
        })
        .catch(() => {
          /* unknown id — the admin just picks the source manually */
        })
        .finally(() => {
          if (!cancelled) setPrefillLoaded(true);
        });
      return () => {
        cancelled = true;
      };
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPrefillLoaded(true);
    return undefined;
  }, [params, searchParams, prefillLoaded, loadPreview]);

  const conflicts: readonly MergeConflict[] = preview?.conflicts ?? [];
  const hasConflicts = conflicts.length > 0;
  const reasonValid = reason.trim().length >= MERGE_REASON_MIN;

  const reparentColumns: readonly TableColumn<[string, number]>[] = [
    {
      key: 'table',
      header: t('admin:venueMerge.merge.preview.tableColumn', { defaultValue: 'Table' }),
      cell: ([table]) => <span className="font-mono text-[12px] text-ink">{table}</span>,
    },
    {
      key: 'count',
      header: t('admin:venueMerge.merge.preview.countColumn', { defaultValue: 'Rows' }),
      align: 'end',
      cell: ([, count]) => <span className="text-[13px] tabular-nums text-ink">{count}</span>,
    },
  ];
  const reparentRows: readonly [string, number][] = Object.entries(preview?.reparent ?? {});

  const swap = () => {
    const prevSource = source;
    setSource(canonical);
    setCanonical(prevSource);
    setStep('select');
    setPreview(null);
  };

  const runMerge = async () => {
    if (!source || !canonical || !reasonValid) return;
    setMerging(true);
    setMergeError(null);
    try {
      await adminVenuesMergeApi.merge({
        sourceVenueId: source.id,
        canonicalVenueId: canonical.id,
        reason: reason.trim(),
      });
      setConfirmOpen(false);
      setStep('done');
      toast.push({
        tone: 'success',
        title: t('admin:venueMerge.merge.result.successTitle', { defaultValue: 'Venues merged' }),
      });
    } catch (err) {
      const code = extractCode(err);
      setMergeError(
        code === 'ALREADY_MERGED'
          ? t('admin:venueMerge.merge.errors.alreadyMerged', {
              defaultValue: 'This source venue is already merged into a different canonical venue.',
            })
          : t('admin:venueMerge.merge.errors.generic', {
              defaultValue: 'The merge could not be completed. Please try again.',
            }),
      );
      setConfirmOpen(false);
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h1 font-bold text-ink">
          {t('admin:venueMerge.merge.pageTitle', { defaultValue: 'Merge venues' })}
        </h1>
        <p className="mt-1 text-body text-ink-2 max-w-2xl">
          {t('admin:venueMerge.merge.intro', {
            defaultValue:
              'Merge a duplicate venue record into a chosen canonical venue. The source venue is never deleted — reads to its old slug and id redirect to the canonical venue.',
          })}
        </p>
      </div>

      <Stepper
        current={stepIndex[step]}
        steps={[
          { label: t('admin:venueMerge.merge.steps.select', { defaultValue: 'Select venues' }) },
          { label: t('admin:venueMerge.merge.steps.preview', { defaultValue: 'Review impact' }) },
          {
            label: t('admin:venueMerge.merge.steps.canonical', { defaultValue: 'Pick canonical' }),
          },
          { label: t('admin:venueMerge.merge.steps.confirm', { defaultValue: 'Confirm' }) },
        ]}
      />

      {/* Step 1 — select */}
      {step === 'select' ? (
        <Card>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <VenueSearchField
              label={t('admin:venueMerge.merge.select.sourceLabel', {
                defaultValue: 'Source venue (merged away)',
              })}
              placeholder={t('admin:venueMerge.merge.select.sourcePlaceholder', {
                defaultValue: 'Search the source venue by name…',
              })}
              testId="merge-source-search"
              excludeId={canonical?.id ?? null}
              selected={source}
              onSelect={setSource}
              onClear={() => setSource(null)}
            />
            <VenueSearchField
              label={t('admin:venueMerge.merge.select.canonicalLabel', {
                defaultValue: 'Canonical venue (kept)',
              })}
              placeholder={t('admin:venueMerge.merge.select.canonicalPlaceholder', {
                defaultValue: 'Search the canonical venue by name…',
              })}
              testId="merge-canonical-search"
              excludeId={source?.id ?? null}
              selected={canonical}
              onSelect={setCanonical}
              onClear={() => setCanonical(null)}
            />
          </div>

          {source && canonical && source.id === canonical.id ? (
            <Banner tone="danger" className="mt-4">
              {t('admin:venueMerge.merge.select.sameVenue', {
                defaultValue: 'Pick two different venues to merge.',
              })}
            </Banner>
          ) : null}

          {previewError ? (
            <Banner tone="danger" className="mt-4">
              {previewError}
            </Banner>
          ) : null}

          <div className="mt-5 flex justify-end">
            <Button
              variant="primary"
              data-testid="merge-step-select-next"
              loading={previewLoading}
              disabled={!source || !canonical || source.id === canonical.id || previewLoading}
              onClick={() => {
                if (source && canonical) void loadPreview(source, canonical);
              }}
            >
              {t('admin:venueMerge.merge.select.next', { defaultValue: 'Review impact' })}
            </Button>
          </div>
        </Card>
      ) : null}

      {/* Step 2 — preview */}
      {step === 'preview' ? (
        <Card>
          <div data-testid="merge-preview" className="space-y-5">
            <h2 className="text-[15px] font-semibold text-ink">
              {t('admin:venueMerge.merge.preview.title', { defaultValue: 'Impact preview' })}
            </h2>

            <div>
              <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-3 mb-2">
                {t('admin:venueMerge.merge.preview.reparentHeading', {
                  defaultValue: 'Rows that will move to the canonical venue',
                })}
              </p>
              {reparentRows.length > 0 ? (
                <Table
                  data={reparentRows}
                  columns={reparentColumns}
                  rowKey={([table]) => table}
                  density="compact"
                  ariaLabel={t('admin:venueMerge.merge.preview.reparentHeading', {
                    defaultValue: 'Rows that will move to the canonical venue',
                  })}
                />
              ) : (
                <p className="text-[12.5px] text-ink-3">
                  {t('admin:venueMerge.merge.preview.reparentEmpty', {
                    defaultValue: 'No dependent rows to move.',
                  })}
                </p>
              )}
            </div>

            <div>
              <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-3 mb-2">
                {t('admin:venueMerge.merge.preview.conflictsHeading', {
                  defaultValue: 'Conflicts that need acknowledgement',
                })}
              </p>
              {hasConflicts ? (
                <div className="space-y-2">
                  {conflicts.map((c, i) => (
                    <Banner key={`${c.table}-${i}`} tone="warning">
                      <span className="flex flex-col gap-0.5">
                        <span className="flex items-center gap-2">
                          <Badge tone="warning" size="sm">
                            {c.kind === 'BILLING_CONFLICT'
                              ? t('admin:venueMerge.merge.preview.conflictBilling', {
                                  defaultValue: 'Billing conflict',
                                })
                              : c.kind === 'SUBSCRIPTION_CONFLICT'
                                ? t('admin:venueMerge.merge.preview.conflictSubscription', {
                                    defaultValue: 'Subscription conflict',
                                  })
                                : t('admin:venueMerge.merge.preview.conflictPaymentConfig', {
                                    defaultValue: 'Payment config conflict',
                                  })}
                          </Badge>
                          <span className="font-mono text-[11px] text-ink-3">{c.table}</span>
                        </span>
                        <span className="text-[12.5px]">{c.detail}</span>
                      </span>
                    </Banner>
                  ))}
                  <label className="flex items-start gap-2 cursor-pointer pt-1">
                    <Checkbox
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                      data-testid="merge-ack-conflicts"
                    />
                    <span className="text-[12.5px] text-ink-2 leading-snug">
                      {t('admin:venueMerge.merge.preview.acknowledge', {
                        defaultValue:
                          "I understand the canonical venue's row is kept and the source's is left inert.",
                      })}
                    </span>
                  </label>
                </div>
              ) : (
                <p className="text-[12.5px] text-ink-3">
                  {t('admin:venueMerge.merge.preview.conflictsEmpty', {
                    defaultValue: 'No conflicts detected.',
                  })}
                </p>
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep('select')}>
                {t('admin:venueMerge.merge.preview.back', { defaultValue: 'Back' })}
              </Button>
              <Button
                variant="primary"
                data-testid="merge-step-preview-next"
                disabled={hasConflicts && !acknowledged}
                onClick={() => setStep('canonical')}
              >
                {t('admin:venueMerge.merge.preview.next', { defaultValue: 'Pick canonical' })}
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Step 3 — canonical + reason */}
      {step === 'canonical' ? (
        <Card>
          <div className="space-y-5">
            <h2 className="text-[15px] font-semibold text-ink">
              {t('admin:venueMerge.merge.canonical.title', {
                defaultValue: 'Confirm the canonical venue',
              })}
            </h2>
            <p className="text-[12.5px] text-ink-2">
              {t('admin:venueMerge.merge.canonical.helper', {
                defaultValue:
                  'The canonical venue is kept; the source venue is marked merged and redirects to it.',
              })}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-md border border-hairline-2 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-3 mb-1">
                  {t('admin:venueMerge.merge.canonical.keptLabel', {
                    defaultValue: 'Kept (canonical)',
                  })}
                </p>
                <p className="text-[13px] font-medium text-ink">{canonical?.name}</p>
                <p className="font-mono text-[11px] text-ink-3">{canonical?.slug}</p>
              </div>
              <div className="rounded-md border border-hairline-2 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-3 mb-1">
                  {t('admin:venueMerge.merge.canonical.mergedLabel', {
                    defaultValue: 'Merged away (source)',
                  })}
                </p>
                <p className="text-[13px] font-medium text-ink">{source?.name}</p>
                <p className="font-mono text-[11px] text-ink-3">{source?.slug}</p>
              </div>
            </div>

            <Button variant="ghost" size="sm" onClick={swap}>
              {t('admin:venueMerge.merge.canonical.swap', {
                defaultValue: 'Swap source and canonical',
              })}
            </Button>

            <FormField
              label={t('admin:venueMerge.merge.canonical.reasonLabel', {
                defaultValue: 'Reason for this merge',
              })}
              helper={t('admin:venueMerge.merge.canonical.reasonHelper', {
                defaultValue: 'Required — recorded in the audit log (min 10 characters).',
              })}
              required
            >
              <Textarea
                rows={3}
                maxLength={2000}
                showCounter
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="merge-reason"
              />
            </FormField>

            {mergeError ? <Banner tone="danger">{mergeError}</Banner> : null}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep('preview')}>
                {t('admin:venueMerge.merge.canonical.back', { defaultValue: 'Back' })}
              </Button>
              <Button
                variant="destructive"
                data-testid="merge-step-canonical-next"
                disabled={!reasonValid}
                onClick={() => setConfirmOpen(true)}
              >
                {t('admin:venueMerge.merge.canonical.next', { defaultValue: 'Confirm merge' })}
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Step 4 — done */}
      {step === 'done' ? (
        <Card>
          <div className="space-y-4" data-testid="merge-result">
            <Banner tone="success">
              {t('admin:venueMerge.merge.result.successBody', {
                defaultValue: 'The source venue now redirects to the canonical venue.',
              })}
            </Banner>
            <div className="flex gap-3">
              <Button
                variant="primary"
                onClick={() => canonical && navigate(`/venues/${canonical.id}`)}
              >
                {t('admin:venueMerge.merge.result.viewCanonical', {
                  defaultValue: 'View canonical venue',
                })}
              </Button>
              <Button variant="ghost" onClick={() => navigate('/venues')}>
                {t('admin:venueMerge.merge.result.startOver', {
                  defaultValue: 'Merge another pair',
                })}
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Confirm dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void runMerge()}
        destructive
        loading={merging}
        title={t('admin:venueMerge.merge.confirm.title', { defaultValue: 'Merge these venues?' })}
        description={t('admin:venueMerge.merge.confirm.body', {
          defaultValue:
            'This re-parents every dependent row to the canonical venue and marks the source venue as merged. This cannot be undone.',
        })}
        confirmLabel={t('admin:venueMerge.merge.confirm.confirmCta', {
          defaultValue: 'Merge venues',
        })}
        cancelLabel={t('admin:venueMerge.merge.confirm.cancel', { defaultValue: 'Cancel' })}
      >
        <p className="text-[12.5px] text-ink-2">
          {source?.name} → {canonical?.name}
        </p>
      </Dialog>
    </div>
  );
}
